// ---------------------------------------------------------------------------
// Rede de recuperação: a parte SUJA (storage, IndexedDB, subscrições).
//
// Desenho, em três frases:
//
//  1. Os stores de trabalho continuam a escrever onde escreviam. O que
//     muda é que esse conteúdo passa a ser um BUFFER DE RECUPERAÇÃO,
//     selado por `vn2r.recovery.manifest`, e não uma biblioteca que
//     reaparece sozinha ao arrancar.
//  2. Ao arrancar, a app fica BLOQUEADA para leituras de trabalho até
//     saber o que fazer: com manifesto pergunta ("recuperar a sessão
//     anterior?"); sem manifesto limpa o buffer em silêncio (é lixo de
//     uma sessão já gravada ou descartada) e segue vazia.
//  3. "Guardar sessão" com sucesso apaga o manifesto: deixa de haver
//     trabalho por gravar, não há aviso ao sair nem diálogo no arranque
//     seguinte, até se mexer outra vez.
//
// Nada aqui importa os stores de trabalho: as bases IndexedDB são
// abertas por nome. É de propósito, para não haver ciclos de import
// (ifcPersistence, bcfPersistence, viewpointsStore e views2dStorage
// importam ESTE ficheiro para marcar sujo).
// ---------------------------------------------------------------------------

import {
  EMPTY_COUNTS,
  RECOVERY_MANIFEST_KEY,
  RECOVERY_MARKER_KEY,
  RECOVERY_MARKER_VALUE,
  buildManifest,
  collectWorkKeys,
  countTriagedInRaw,
  countViewsInRaw,
  isClashTriageKey,
  isViews2dKey,
  isWorkKey,
  manifestIsEmpty,
  parseManifest,
  type RecoveryCounts,
  type RecoveryManifest,
} from './recoveryManifest'

/** Bases IndexedDB que guardam TRABALHO. Abertas por nome para este
 *  módulo não depender dos stores (e eles poderem depender dele). */
const WORK_DBS = [
  { db: 'vn2r.ifcs', store: 'bytes', field: 'ifcs' as const },
  { db: 'vn2r-bcf', store: 'loaded', field: 'bcfs' as const },
  { db: 'vn2r.viewpoints', store: 'viewpoints', field: 'viewpoints' as const },
]

/** Chave de sessionStorage com o handoff de viewpoint pendente. É
 *  transitória (morre com o separador), mas num "descartar" também sai. */
const PENDING_VIEWPOINT_KEY = 'vn2r.bcf.pendingViewpointId'

export type RecoveryPhase =
  /** A decidir o que fazer. Leituras de trabalho bloqueadas. */
  | 'checking'
  /** Há um manifesto: à espera da resposta do utilizador. */
  | 'pending'
  /** Decidido (recuperado, descartado ou nada havia). */
  | 'ready'

export interface RecoveryStatus {
  phase: RecoveryPhase
  /** O que está no buffer, quando `phase === 'pending'`. */
  manifest: RecoveryManifest | null
  /** Verdadeiro quando o utilizador aceitou recuperar nesta sessão. */
  adopted: boolean
  /** Há trabalho por gravar NESTA sessão (marca na barra + aviso ao sair). */
  dirty: boolean
}

let status: RecoveryStatus = {
  phase: 'checking',
  manifest: null,
  adopted: false,
  dirty: false,
}

const listeners = new Set<() => void>()
let started = false
let resolveReady: ((adopted: boolean) => void) | null = null
// `const` desde que o único sítio que a reatribuía, um helper de reset
// para testes que ninguém chegou a usar, saiu daqui.
const readyPromise: Promise<boolean> = new Promise((res) => {
  resolveReady = res
})
let rebuildTimer: ReturnType<typeof setTimeout> | null = null
const knownIfcNames = new Set<string>()
/** O manifesto tal como estava no arranque, antes de esta sessão abrir
 *  alguma coisa. O `status.manifest` continua a ser reescrito enquanto a
 *  pergunta está no ecrã (é a rede para um novo fecho da aba), e a
 *  pergunta mostrava os IFC desta sessão como se fossem da anterior. */
let manifestoDoArranque: RecoveryManifest | null = null

/** O que a sessão ANTERIOR deixou, fixado no arranque. */
export function getManifestoDoArranque(): RecoveryManifest | null {
  return manifestoDoArranque
}

function setStatus(patch: Partial<RecoveryStatus>): void {
  status = { ...status, ...patch }
  for (const l of listeners) l()
}

function markReady(adopted: boolean): void {
  setStatus({ phase: 'ready', adopted, manifest: adopted ? status.manifest : null })
  resolveReady?.(adopted)
  resolveReady = null
}

// --- acesso defensivo ao storage -------------------------------------------

function ls(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

function safeGet(key: string): string | null {
  try {
    return ls()?.getItem(key) ?? null
  } catch {
    return null
  }
}

function safeSet(key: string, value: string): void {
  try {
    ls()?.setItem(key, value)
  } catch {
    /* quota cheia ou modo privado: sem rede, mas a app funciona */
  }
}

function safeRemove(key: string): void {
  try {
    ls()?.removeItem(key)
  } catch {
    /* ignorar */
  }
}

function allLocalKeys(): string[] {
  const store = ls()
  if (!store) return []
  const out: string[] = []
  try {
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i)
      if (k) out.push(k)
    }
  } catch {
    return []
  }
  return out
}

// --- IndexedDB genérico (sem importar os stores) ---------------------------

/**
 * Abre uma base SÓ SE ELA JÁ EXISTIR.
 *
 * Um `indexedDB.open(nome)` normal CRIA a base quando ela não existe, e
 * cria-a vazia, sem object store nenhuma. Isso partia o dono da base:
 * o `bcfPersistence` abre a mesma versão 1, o `onupgradeneeded` já não
 * dispara, a store 'loaded' nunca chega a ser criada e todas as
 * transacções rebentam com NotFoundError. Daí a dupla defesa: primeiro
 * `databases()` (quando existe), e como rede o `onupgradeneeded` a
 * denunciar a criação, que desfazemos logo.
 */
async function openExisting(name: string): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return null
  try {
    if (typeof indexedDB.databases === 'function') {
      const list = await indexedDB.databases()
      if (!list.some((d) => d.name === name)) return null
    }
  } catch {
    /* sem `databases()` fiável: segue para a via defensiva */
  }
  return new Promise((resolve) => {
    try {
      let created = false
      const req = indexedDB.open(name)
      req.onupgradeneeded = () => {
        created = true
      }
      req.onsuccess = () => {
        const db = req.result
        if (created) {
          db.close()
          try {
            indexedDB.deleteDatabase(name)
          } catch {
            /* ignorar */
          }
          resolve(null)
          return
        }
        resolve(db)
      }
      req.onerror = () => resolve(null)
      req.onblocked = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

/** Base existente mas sem a store esperada = artefacto sem valor
 *  (ninguém a criou pelo caminho normal). Apaga-se para o dono da base
 *  a poder criar em condições no próximo `open`. */
function dropBrokenDb(db: IDBDatabase, name: string): void {
  db.close()
  try {
    indexedDB.deleteDatabase(name)
  } catch {
    /* ignorar */
  }
}

async function countIdb(name: string, store: string): Promise<number> {
  const db = await openExisting(name)
  if (!db) return 0
  try {
    if (!db.objectStoreNames.contains(store)) {
      dropBrokenDb(db, name)
      return 0
    }
    const n = await new Promise<number>((resolve) => {
      try {
        const tx = db.transaction(store, 'readonly')
        const req = tx.objectStore(store).count()
        req.onsuccess = () => resolve(req.result ?? 0)
        req.onerror = () => resolve(0)
      } catch {
        resolve(0)
      }
    })
    db.close()
    return n
  } catch {
    try {
      db.close()
    } catch {
      /* ignorar */
    }
    return 0
  }
}

async function clearIdb(name: string, store: string): Promise<void> {
  const db = await openExisting(name)
  if (!db) return
  try {
    if (!db.objectStoreNames.contains(store)) {
      dropBrokenDb(db, name)
      return
    }
    await new Promise<void>((resolve) => {
      try {
        const tx = db.transaction(store, 'readwrite')
        tx.objectStore(store).clear()
        tx.oncomplete = () => resolve()
        tx.onerror = () => resolve()
        tx.onabort = () => resolve()
      } catch {
        resolve()
      }
    })
    db.close()
  } catch {
    try {
      db.close()
    } catch {
      /* ignorar */
    }
  }
}

// --- varrimento e limpeza do buffer ----------------------------------------

/** Parte síncrona do varrimento: só localStorage (vistas 2D e
 *  rascunhos). Serve o selo imediato do `markWorkDirty`. */
function scanLocalWork(): { counts: RecoveryCounts; keys: string[] } {
  const counts: RecoveryCounts = { ...EMPTY_COUNTS }
  const keys = collectWorkKeys(allLocalKeys())
  for (const k of keys) {
    if (isViews2dKey(k)) counts.views2d += countViewsInRaw(safeGet(k))
    else if (isClashTriageKey(k)) {
      counts.clash = (counts.clash ?? 0) + countTriagedInRaw(safeGet(k))
    } else counts.drafts += 1
  }
  return { counts, keys }
}

/** Conta o que está no buffer, sem o alterar. */
export async function scanWorkStorage(): Promise<{
  counts: RecoveryCounts
  keys: string[]
}> {
  const { counts, keys } = scanLocalWork()
  for (const spec of WORK_DBS) {
    counts[spec.field] = await countIdb(spec.db, spec.store)
  }
  return { counts, keys }
}

/** Apaga TODO o trabalho persistido: bases IndexedDB, chaves de
 *  localStorage de trabalho e o manifesto. Nunca toca em preferências. */
export async function wipeWorkStorage(): Promise<void> {
  for (const k of allLocalKeys()) {
    if (isWorkKey(k)) safeRemove(k)
  }
  try {
    sessionStorage.removeItem(PENDING_VIEWPOINT_KEY)
  } catch {
    /* ignorar */
  }
  safeRemove(RECOVERY_MANIFEST_KEY)
  knownIfcNames.clear()
  for (const spec of WORK_DBS) {
    await clearIdb(spec.db, spec.store)
  }
}

// --- manifesto --------------------------------------------------------------

function writeManifest(m: RecoveryManifest): void {
  safeSet(RECOVERY_MANIFEST_KEY, JSON.stringify(m))
}

async function rebuildManifest(): Promise<void> {
  // Um "gravar sessão" a meio do debounce deixa de haver o que selar.
  if (!status.dirty) return
  const { counts, keys } = await scanWorkStorage()
  const m = buildManifest({
    counts,
    keys,
    ifcNames: Array.from(knownIfcNames),
  })
  if (manifestIsEmpty(m)) {
    // Ficou tudo vazio (fechou o último BCF, apagou a última vista):
    // não há nada por gravar, logo não há aviso ao sair.
    safeRemove(RECOVERY_MANIFEST_KEY)
    setStatus({ manifest: null, dirty: false })
    return
  }
  writeManifest(m)
  setStatus({ manifest: m })
}

/**
 * Chamado por cada store de trabalho depois de escrever. Marca a sessão
 * como "por gravar" e agenda a actualização do manifesto (as contagens
 * exigem varrer o IndexedDB, por isso vão com atraso; o selo mínimo é
 * escrito já, para um crash imediato não ficar sem rede).
 */
export function markWorkDirty(opts?: { ifcName?: string }): void {
  if (opts?.ifcName) knownIfcNames.add(opts.ifcName)
  if (!status.dirty) {
    setStatus({ dirty: true })
    // Selo mínimo imediato (só o que se lê em síncrono): as contagens
    // completas chegam no rebuild, 600 ms depois. Um crash pelo meio
    // fica com uma rede incompleta, nunca com rede nenhuma.
    if (!safeGet(RECOVERY_MANIFEST_KEY)) {
      const { counts, keys } = scanLocalWork()
      counts.ifcs = knownIfcNames.size
      const seal = buildManifest({
        counts,
        keys,
        ifcNames: Array.from(knownIfcNames),
      })
      if (!manifestIsEmpty(seal)) writeManifest(seal)
    }
  }
  if (rebuildTimer) clearTimeout(rebuildTimer)
  rebuildTimer = setTimeout(() => {
    rebuildTimer = null
    void rebuildManifest()
  }, 600)
}

/**
 * Um modelo saiu da sessão (Remover, Substituir): deixa de ser trabalho
 * a recuperar. Sai o nome da lista do manifesto e, quando `sha256` vem,
 * os bytes da cache que o "Recuperar" reabre (`ifcPersistence`). Quem
 * chama decide se outro modelo aberto ainda usa o mesmo ficheiro e, nesse
 * caso, passa null. Sem isto o "Recuperar" reabria tudo o que alguma vez
 * se abriu na sessão, incluindo a versão que foi substituída.
 */
export async function esquecerIfc(opts: {
  sha256: string | null
  nome: string | null
}): Promise<void> {
  if (!opts.sha256 && !opts.nome) return
  if (opts.nome) knownIfcNames.delete(opts.nome)
  if (opts.sha256) {
    // Import dinâmico: o ifcPersistence importa este módulo.
    const { ifcPersistence } = await import('@core/session/ifcPersistence')
    await ifcPersistence.remove(opts.sha256)
  }
  markWorkDirty()
}

/**
 * "Guardar sessão" correu bem: o trabalho deixa de estar por gravar.
 * O buffer fica onde está (rede para um crash a seguir), mas sem selo
 * já não é oferecido no arranque seguinte.
 */
export function markWorkSaved(): void {
  if (rebuildTimer) {
    clearTimeout(rebuildTimer)
    rebuildTimer = null
  }
  safeRemove(RECOVERY_MANIFEST_KEY)
  setStatus({ dirty: false, manifest: null })
}

// --- arranque ---------------------------------------------------------------

/**
 * Corre uma vez, o mais cedo possível (main.tsx). A parte síncrona
 * decide já se há manifesto, para nenhuma leitura de trabalho passar
 * antes de a pergunta estar no ecrã.
 */
export function initRecovery(): void {
  if (started) return
  started = true
  const manifest = parseManifest(safeGet(RECOVERY_MANIFEST_KEY))
  if (manifest && !manifestIsEmpty(manifest)) {
    for (const n of manifest.ifcNames) knownIfcNames.add(n)
    manifestoDoArranque = manifest
    setStatus({ phase: 'pending', manifest })
    return
  }
  void bootAsync()
}

async function bootAsync(): Promise<void> {
  const marker = safeGet(RECOVERY_MARKER_KEY)
  if (!marker) {
    // MIGRAÇÃO: primeira vez com o código novo. O que já estava
    // guardado por estas chaves não pode desaparecer, ganha manifesto
    // e é oferecido como recuperação.
    const { counts, keys } = await scanWorkStorage()
    safeSet(RECOVERY_MARKER_KEY, RECOVERY_MARKER_VALUE)
    const m = buildManifest({ counts, keys })
    if (!manifestIsEmpty(m)) {
      writeManifest(m)
      console.info(
        '[recovery] trabalho anterior encontrado sem manifesto: migrado para recuperação',
        m.counts,
      )
      manifestoDoArranque = m
      setStatus({ phase: 'pending', manifest: m })
      return
    }
    markReady(false)
    return
  }
  // Marca presente e sem manifesto: o que lá está é resto de uma sessão
  // já gravada (ou descartada). Limpa-se em silêncio para nada
  // reaparecer sozinho.
  const { counts } = await scanWorkStorage()
  if (!manifestIsEmpty(buildManifest({ counts, keys: [] }))) {
    console.info('[recovery] buffer sem manifesto: limpo no arranque', counts)
    await wipeWorkStorage()
  }
  markReady(false)
}

/** O utilizador aceitou recuperar. Desbloqueia as leituras; quem
 *  repõe o conteúdo é o RecoveryPrompt. */
export function acceptRecovery(): void {
  setStatus({ phase: 'ready', adopted: true, dirty: true })
  resolveReady?.(true)
  resolveReady = null
  avisarTrabalhoReposto()
}

/** O utilizador descartou. Apaga tudo o que é trabalho (IndexedDB
 *  incluído) e o manifesto. As preferências ficam intactas. */
export async function discardRecovery(): Promise<void> {
  await wipeWorkStorage()
  setStatus({ phase: 'ready', adopted: false, manifest: null, dirty: false })
  resolveReady?.(false)
  resolveReady = null
}

/** "Fechar sessão": limpa o buffer e o selo sem passar pelo diálogo. */
export async function resetRecovery(): Promise<void> {
  if (rebuildTimer) {
    clearTimeout(rebuildTimer)
    rebuildTimer = null
  }
  await wipeWorkStorage()
  setStatus({ phase: 'ready', adopted: false, manifest: null, dirty: false })
  resolveReady?.(false)
  resolveReady = null
}

// --- leitura do estado ------------------------------------------------------

export function getRecoveryStatus(): RecoveryStatus {
  return status
}

export function subscribeRecovery(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

/**
 * Espera pela decisão do arranque. `true` = o utilizador recuperou.
 *
 * É este o portão das hidratações (BCFs, viewpoints): não gateiam pelo
 * valor, gateiam pela ESPERA. Quando a decisão é "descartar" (ou não
 * havia nada a decidir), o buffer já foi limpo antes de a promessa
 * resolver, por isso quem hidrata a seguir não encontra nada.
 */
export function whenRecoveryResolved(): Promise<boolean> {
  if (!started || status.phase === 'ready') {
    return Promise.resolve(status.adopted)
  }
  return readyPromise
}

/** Verdadeiro enquanto ninguém decidiu: as leituras síncronas de
 *  trabalho (vistas 2D, rascunhos) devolvem vazio neste intervalo.
 *  Sem `initRecovery` (testes em Node) nunca bloqueia. */
export function isWorkReadBlocked(): boolean {
  return started && status.phase !== 'ready'
}

export function hasUnsavedWork(): boolean {
  return status.dirty
}

// --- fotografia do trabalho que vive em localStorage -----------------------
// Usada pelo "Guardar sessão" (v5): sem isto, gravar não preservava as
// vistas 2D nem os rascunhos e a promessa "grava e não perdes" era
// falsa.

export interface LocalWorkSnapshot {
  key: string
  raw: string
}

export function collectLocalWorkSnapshot(): LocalWorkSnapshot[] {
  const out: LocalWorkSnapshot[] = []
  for (const key of collectWorkKeys(allLocalKeys())) {
    const raw = safeGet(key)
    if (raw) out.push({ key, raw })
  }
  return out
}

export function applyLocalWorkSnapshot(items: LocalWorkSnapshot[]): void {
  for (const item of items) {
    if (!isWorkKey(item.key)) continue
    safeSet(item.key, item.raw)
  }
  avisarTrabalhoReposto()
}

/** Quem guarda trabalho num store em memória (os cortes) e o tem de
 *  reler quando o localStorage muda por baixo: abrir uma sessão, abrir
 *  uma sessão partilhada, aceitar a recuperação. As vistas 2D lêem do
 *  armazenamento quando precisam e não precisam disto. */
const ouvintesDaReposicao = new Set<() => void>()

export function aoReporTrabalho(cb: () => void): () => void {
  ouvintesDaReposicao.add(cb)
  return () => {
    ouvintesDaReposicao.delete(cb)
  }
}

function avisarTrabalhoReposto(): void {
  for (const cb of ouvintesDaReposicao) {
    try {
      cb()
    } catch (err) {
      console.warn('[recovery] reposição do trabalho falhou num ouvinte', err)
    }
  }
}

